import { MessageBubble, messageVariants } from "./Chat.styles";
import { Typography, Box, Dialog, IconButton, Button, CircularProgress } from "@mui/material";
import { Message, MessageStatus } from "../../types/chat";
import { WelcomeMessage, MarkdownWrapper} from "./Chat.styles";
import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm';
import { DataTable } from "../Table/DataTable";
import { useEffect, useRef, useCallback, useState } from 'react';
import { MessageActions } from './MessageActions';
import MyPlotComponent from '../Chart/renderPlotly';
import { Close } from '@mui/icons-material';
import { useOnboarding } from '../../context/OnboardingContext';
import { useTranslation } from 'react-i18next';
import CustomLink from './CustomLink';
import { ProgressMetadata } from '../../types/chat';
import { useInvalidLinks } from '../../context/InvalidLinksContext';
import { useApi } from '../../hooks/useApi';
import { UsageStatsDisplay } from './UsageStatsDisplay';

interface ChatMessageProps {
  message: Message;
  isFirstMessage: boolean;
  isFullscreen: boolean;
  isSidePanel: boolean;
  isChatMode: boolean;
  onRetry?: (checkpointId: string, messageContent: string) => Promise<void>;
  hasError?: boolean;
  progressData?: ProgressMetadata;
  currentSentence?: string;
  question?: string;
  isTrial?: boolean;
  organizationId?: string;
  isTrialBannerVisible?: boolean;
  trialDaysRemaining?: number;
}

// Component to render plain text links during streaming
const PlainTextLink = ({ href, children }: React.ComponentPropsWithoutRef<'a'>) => {
  // Always render as plain text during streaming
  return <span className="streaming-link">{children}</span>;
};

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, isFirstMessage, isFullscreen, isSidePanel, isChatMode, onRetry, hasError, progressData, currentSentence, question, isTrial, organizationId, isTrialBannerVisible, trialDaysRemaining }) => {
  const plotContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const plotInstanceRef = useRef<any>(null);
  const [isFullscreenPlot, setIsFullscreenPlot] = useState(false);
  const fullscreenPlotContainerRef = useRef<HTMLDivElement>(null);
  const fullscreenPlotInstanceRef = useRef<any>(null);
  const { resetAndOpen, hasSeenOnboarding, getHasSeenOnboardingLocal } = useOnboarding();
  const [currentProgress, setCurrentProgress] = useState(0);
  const { invalidLinks, clearInvalidLinks, hasReportedForTrace, markTraceAsReported, addInvalidLinkForPDF } = useInvalidLinks();
  const { fetchWithSession } = useApi();
  const [showSessionExpired, setShowSessionExpired] = useState(false);

  useEffect(() => {
    if (!progressData?.progressEstimate || message.status !== MessageStatus.Streaming) {
      setCurrentProgress(0);
      return;
    }

    const totalEstimate = progressData.progressEstimate;
    const interval = setInterval(() => {
      setCurrentProgress(prev => {
        // Increment by roughly 1/totalEstimate per second, capped at 99%
        const increment = (100 / totalEstimate) / 10; // divide by 10 for smoother updates
        return Math.min(prev + increment, 99);
      });
    }, 100); // Update every 100ms for smooth animation

    return () => clearInterval(interval);
  }, [progressData?.progressEstimate, message.status]);

  const resizePlot = useCallback(() => {
    if (plotInstanceRef.current && window.Plotly) {
      const plotDiv = plotInstanceRef.current.el;
      if (plotDiv && plotDiv.offsetParent !== null) {
        try {
          setTimeout(() => {
            if (plotInstanceRef.current && plotInstanceRef.current.el) {
              (window.Plotly as any).Plots.resize(plotInstanceRef.current);
            }
          }, 100);
        } catch (error) {
          console.warn('Failed to resize plot:', error);
        }
      }
    }
  }, []);
  // Update the ResizeObserver effect
  useEffect(() => {
    if (plotContainerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
                // Add a small delay to ensure the container has settled
        requestAnimationFrame(() => {
          resizePlot();
        });
      });
      
      resizeObserver.observe(plotContainerRef.current);
      return () => {
        resizeObserver.disconnect();
        if (plotInstanceRef.current && window.Plotly) {
          try {
            (window.Plotly as any).purge(plotInstanceRef.current);
          } catch (error) {
            console.warn('Failed to purge plot:', error);
          }
          plotInstanceRef.current = null;
        }
      };
    }
  }, [resizePlot]);
  // Listen for view mode changes and resize accordingly
  useEffect(() => {
  // Small delay to ensure all plots get resized

    setTimeout(resizePlot, 150);
  }, [isFullscreen, isSidePanel, isChatMode, resizePlot]);

  // Handle dialog close
  const handleCloseFullscreen = useCallback(() => {
        // Ensure main plot is resized after dialog closes
    setIsFullscreenPlot(false);
    setTimeout(() => {
      resizePlot();
    }, 100);
  }, [resizePlot]);

  // Send invalid links to backend when message finishes streaming
  useEffect(() => {
    let isMounted = true;
    
    const reportInvalidLinks = async () => {
      // All invalid links should be in the message content
      const invalidLinksExistInMessage = invalidLinks.every(link => message.content.includes(link));
      if (
        message.sender === 'ai' && 
        message.status === MessageStatus.Sent && 
        invalidLinks.length > 0 &&
        message.trace_id && // Only report if we have a trace_id
        !hasReportedForTrace(message.trace_id) && // Only report if we haven't already reported for this trace
        invalidLinksExistInMessage // Only report if all invalid links are in the message content
      ) {
        try {
          // Add a small delay to allow any pending link validations to complete
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Check if component is still mounted before proceeding
          if (!isMounted) return;
          
          // Mark this trace as reported before making the request
          // to prevent race conditions with multiple components
          markTraceAsReported(message.trace_id);
          
          await fetchWithSession(`/api/v1/feedback/invalid-links`, {
            method: 'POST',
            body: JSON.stringify({
              trace_id: message.trace_id,
              invalid_links: invalidLinks,
            }),
          });

          addInvalidLinkForPDF(invalidLinks);
          
          // We can still clear invalid links after reporting
          clearInvalidLinks();
        } catch (error) {
          console.error('Error reporting invalid links:', error);
        }
      }
    };

    reportInvalidLinks();
    
    return () => {
      isMounted = false;
    };
  }, [message.status, message.sender, message.trace_id, invalidLinks, fetchWithSession, hasReportedForTrace, markTraceAsReported, clearInvalidLinks, addInvalidLinkForPDF]);

  // Check if message content contains token expiration warning
  useEffect(() => {
    if (message.sender === 'ai' && message.status === MessageStatus.Error) {
      const isTokenExpired = 
        message.content.includes("session has expired") || 
        message.content.includes("token expired") || 
        message.content.includes("TOKEN_EXPIRED");
      
      if (isTokenExpired) {
        setShowSessionExpired(true);
      }
    }
  }, [message.content, message.status, message.sender]);

  // Handle page refresh
  const handleRefreshPage = () => {
    window.location.reload();
  };

  // Only show welcome message for successful first AI messages and when user hasn't seen onboarding
  if (isFirstMessage && message.sender === 'ai' && message.status === MessageStatus.Sent) {
    return (
      <WelcomeMessage
        key={message.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        style={{ 
          paddingTop: isTrialBannerVisible ? '16px' : '40px' 
        }}
      >
        <Typography variant="h1">{message.content}</Typography>
        
        {/* Usage Stats - shown between welcome message and help button */}
        {isTrial && (
          <UsageStatsDisplay
            organizationId={organizationId}
            variant="empty"
            isCompact={isChatMode}
            daysRemaining={trialDaysRemaining}
          />
        )}
        
        {!hasSeenOnboarding && !getHasSeenOnboardingLocal() && (
          <div className="welcome-extra-actions">
            <Button 
              onClick={resetAndOpen} 
              className="help-started-button"
              variant="contained"
              color="primary"
              sx={{
                borderRadius: '20px',
                padding: '8px 24px',
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                }
              }}
            >
              {t('helpMeInGettingStarted')}
            </Button>
          </div>
        )}
      </WelcomeMessage>
    );
  }

  // Display session expired message with refresh button
  if (showSessionExpired) {
    return (
      <MessageBubble
        key={message.id}
        isUser={false}
        isFullscreen={isFullscreen}
        isSidePanel={isSidePanel}
        role="listitem"
        variants={messageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        isStreaming={false}
        style={{ backgroundColor: "#FFF8E1", border: "1px solid #FFB74D" }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" color="error">
            Session Expired
          </Typography>
          <Typography>
            Your session has expired. Please refresh the page to continue using the chat assistant.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleRefreshPage}
            sx={{ mt: 1 }}
          >
            Refresh Page
          </Button>
        </Box>
      </MessageBubble>
    );
  }

  // Token will expire soon warning
  const isTokenWarning = message.sender === 'ai' && message.content.includes("session will expire soon");
  
  const typingOrProgress = () =>{
    if (message.status === MessageStatus.Streaming && !progressData) {
      return (
        <Box sx={{ opacity: 0.5 }}>
          <span className="cursor">▋</span>
        </Box>
      );
    }
    else if (message.status === MessageStatus.Streaming && progressData && progressData?.waitingSentences?.length > 0) {
      return (
        <Box 
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              padding: 2,
              backgroundColor: '#f0f0f0',
              borderRadius: 2,
              margin: '0 auto',
              maxWidth: '600px'
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2">
              {currentSentence}
              {progressData?.progressEstimate && ` (${Math.round(currentProgress)}%)`}
            </Typography>
          </Box>
      )
    }
    else{
      return (<></>)
    }
  }

  // Choose the appropriate link component based on message status
  const getLinkComponent = () => {
    return message.status === MessageStatus.Streaming ? PlainTextLink : CustomLink;
  };

  return (
    <>
      <MessageBubble
        key={message.id}
        isUser={message.sender === 'user'}
        isFullscreen={isFullscreen}
        isSidePanel={isSidePanel}
        role="listitem"
        variants={messageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        isStreaming={message.status === MessageStatus.Streaming}
        style={isTokenWarning ? { backgroundColor: "#FFF8E1", border: "1px solid #FFB74D" } : undefined}
      >
        {/* Token warning banner */}
        {isTokenWarning && (
          <Box sx={{ 
            bgcolor: 'warning.light', 
            p: 1, 
            mb: 2, 
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="body2" color="warning.dark">
              {message.content}
            </Typography>
            <Button 
              size="small" 
              variant="outlined" 
              color="warning" 
              onClick={handleRefreshPage}
            >
              Refresh Now
            </Button>
          </Box>
        )}

        <Box flex={1}>
          {!isTokenWarning && (
            <>
              {message.generated_html ? (
                <>
                  {message.content && (
                    <Box mb={2}>
                      <MarkdownWrapper>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: getLinkComponent() }}>
                          {message.content}
                        </ReactMarkdown>
                      </MarkdownWrapper>
                    </Box>
                  )}
                  <Box 
                    ref={plotContainerRef}
                    data-message-id={message.id}
                    className="plotly-chart"
                    sx={{ 
                      width: '100%',
                      position: 'relative',
                      border: '1px solid #eee',
                      borderRadius: 1,
                      overflow: 'hidden',
                      '& > div': {
                        width: '100% !important',
                        height: '400px !important',
                      },
                    }}
                  >
                    <MyPlotComponent 
                      message={message} 
                      plotInstanceRef={plotInstanceRef}
                      plotContainerRef={plotContainerRef}
                      viewMode={{ isFullscreen, isSidePanel, isChatMode }}
                    />
                  </Box>
                </>
              ) : message.tableData ? (
                <>
                  {message.content && (
                    <Box mb={2}>
                      <MarkdownWrapper>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: getLinkComponent() }}>
                          {message.content}
                        </ReactMarkdown>
                      </MarkdownWrapper>
                    </Box>
                  )}
                  <DataTable
                    columns={message.tableData.columns}
                    data={message.tableData.data}
                    metadata={message.tableData.metadata}
                  />
                </>
              ) : message.isMarkdown ? (
                <MarkdownWrapper>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: getLinkComponent() }}>
                    {message.content}
                  </ReactMarkdown>
                </MarkdownWrapper>
              ) : (
                <Typography>{message.content}</Typography>
              )}
            </>
          )}
        </Box>

        {typingOrProgress()}

        <Box display="flex" justifyContent="flex-start" alignItems="flex-start">
          <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', marginTop: 1 }}>
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Typography>
          {message.sender === 'ai' && message.status !== MessageStatus.Streaming && (
            <MessageActions 
              messageId={message.id} 
              content={message.content}
              hasPlotly={!!message.generated_html}
              onFullscreen={() => setIsFullscreenPlot(true)}
              traceId={message.trace_id || ''}
              isError={hasError || message.status === MessageStatus.Error}
              onRetry={(hasError || message.status === MessageStatus.Error) ? 
                () => onRetry?.(message.checkpoint_id || '', message.content) : 
                undefined}
              question={question}
            />
          )}
        </Box>
      </MessageBubble>

      <Dialog
        open={isFullscreenPlot}
        onClose={handleCloseFullscreen}
        maxWidth="lg"
        fullScreen
        sx={{
          zIndex: 1000000000000,
        }}
      >
        <Box 
          sx={{ 
            width: '90vw', 
            height: '100vh', 
            p: 2,
            bgcolor: 'background.paper',
            position: 'relative'
          }}
        >
          <IconButton
            onClick={handleCloseFullscreen}
            sx={{
              position: 'absolute',
              left: 8,
              top: 8,
              zIndex: 1
            }}
            aria-label="close fullscreen"
          >
            <Close />
          </IconButton>

          <Box 
            ref={fullscreenPlotContainerRef}
            sx={{ 
              width: '100%', 
              height: '100%',
              position: 'relative'
            }}
          >
            <MyPlotComponent 
              message={message}
              plotInstanceRef={fullscreenPlotInstanceRef}
              plotContainerRef={fullscreenPlotContainerRef}
              isFullscreenDialog={true}
            />
          </Box>
        </Box>
      </Dialog>
    </>
  );
};